/**
 * Shared class strings for the bordered form controls — `TextField`, `Select`'s trigger and both
 * halves of `PhoneInput`.
 *
 * **State precedence:** `disabled` > active (`:focus-visible` or an open popup) > invalid >
 * default.
 *
 * Focus and validation errors both paint the same 4px top border, so one has to yield. Precedence
 * is expressed by scoping the *invalid* rule with `not-*` rather than by out-specifying it:
 *
 *     focus-visible:border-interactive-louder
 *     data-invalid:not-focus-visible:border-negative
 *
 * The two selectors can never match at once, so the winner does not depend on Tailwind's variant
 * emission order. That order was the original bug (APO-3989): `data-*` variants are emitted after
 * pseudo-class variants, so at equal specificity the error rule silently overrode focus and an
 * invalid field showed no focus indicator at all.
 *
 * The error is never hidden — `Field.Error`'s message and icon sit below the control and are
 * unaffected by focus.
 *
 * Add a new active state (a third thing that should outrank an error) by adding it to the `not-*`
 * chain on the invalid rule as well as giving it its own active rule.
 */
/** `TextField`, and `PhoneInput`'s number input (which adds `min-w-0` at its call site). */
export const fieldControlClasses = [
    'bg-interactive text-body-m text-static placeholder:text-static-quiet h-12 w-full border-0 border-t-4 border-transparent px-4 outline-none',
    'focus-visible:border-interactive-louder',
    'disabled:cursor-not-allowed disabled:opacity-60',
    'data-invalid:not-focus-visible:border-negative',
].join(' ');
/** `Select`'s trigger. */
export const fieldTriggerClasses = [
    'bg-interactive text-body-m text-static flex h-12 w-full cursor-pointer items-center justify-between gap-2 border-0 border-t-4 border-transparent px-4 outline-none',
    'focus-visible:border-interactive-louder',
    // Mouse clicks don't trigger :focus-visible in every browser, and an open popup moves focus
    // into the listbox, so the open dropdown itself drives the same active border.
    'data-popup-open:border-interactive-louder',
    'disabled:cursor-not-allowed disabled:opacity-60',
    'data-invalid:not-focus-visible:not-data-popup-open:border-negative',
].join(' ');
/**
 * `PhoneInput`'s country trigger — the left half of the fused field. Only `border-t` ever changes
 * colour, so the 1px `border-r` divider between the two halves stays permanently transparent.
 *
 * Each half owns its own error border: focusing this trigger turns *this* half active while the
 * number input beside it keeps its red border, so the error stays visible on the half that is
 * actually invalid.
 */
export const fieldFusedTriggerClasses = [
    'bg-interactive text-body-m text-static flex h-12 shrink-0 cursor-pointer items-center gap-1.5 border-0 border-t-4 border-r border-transparent px-3 outline-none',
    // The number field is a separate control, so its focus never reaches this trigger's own
    // :focus-visible — group-has-focus-visible mirrors the active state across, fusing the two
    // halves into what reads as one bordered field. It covers this trigger's own focus too, since
    // the trigger is itself a descendant of the group.
    'group-has-focus-visible:border-t-interactive-louder',
    'data-popup-open:border-t-interactive-louder',
    'disabled:cursor-not-allowed disabled:opacity-60',
    // The number field's data-invalid is the real source of truth (it already combines the `error`
    // prop with any Form-injected error) — bridge it across via `group` rather than re-deriving it.
    'group-has-data-invalid:not-group-has-focus-visible:not-data-popup-open:border-t-negative',
].join(' ');
